import { Stack } from "@mui/material";
import { TableCardContentProps, TableCardEntry } from "src/components/cards/senec-tile/dataTypes";
import {
    TableEntryIcon,
    TableEntryLabel,
    TableEntryValue,
} from "src/components/cards/senec-tile/table-card-content/TableCardElements";
import { useScreenSize } from "src/hooks/useScreenSize";
import { StackedCardContent } from "src/components/cards/senec-tile/table-card-content/stacked-card-content/StackedCardContent";
import { dataHasIcons } from "src/components/cards/senec-tile/table-card-content/tableCardUtils";

export const TableCardContent = ({
    data,
    highlightLabel,
    hasIconPlaceholderSpacing = false,
    horizontal,
}: TableCardContentProps & { hasIconPlaceholderSpacing?: boolean }) => {
    const { isMobile } = useScreenSize();
    const placeholderSpacing = hasIconPlaceholderSpacing || dataHasIcons(data);

    return isMobile ? (
        <StackedCardContent data={data} hasIconPlaceholderSpacing={placeholderSpacing} highlightLabel={highlightLabel} />
    ) : (
        <Stack width={"100%"} spacing={2}>
            {data.map((entry: TableCardEntry, index) => (
                <Stack key={"table-card-entry-" + index}>
                    {horizontal ? (
                        <HorizontalEntry
                            data={entry}
                            hasIconPlaceholderSpacing={placeholderSpacing}
                            highlightLabel={highlightLabel}
                        />
                    ) : (
                        <VerticalEntry
                            data={entry}
                            hasIconPlaceholderSpacing={placeholderSpacing}
                            highlightLabel={highlightLabel}
                        />
                    )}
                </Stack>
            ))}
        </Stack>
    );
};

/**
 * Responsive Vertical entry:
 * data and label displayed in rows until minWidth is reached,
 * then flexes to be in column similar to MobileTableEntry
 */
const VerticalEntry = ({
    data,
    hasIconPlaceholderSpacing,
    highlightLabel,
}: {
    data: TableCardEntry;
    hasIconPlaceholderSpacing: boolean;
    highlightLabel?: boolean;
}) => {
    return (
        <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
            <Stack spacing={1} direction="row" flexWrap="wrap" useFlexGap flexGrow={1}>
                <Stack width="25%" minWidth="140px" flexGrow={1}>
                    <TableEntryLabel data={data} highlightLabel={highlightLabel} />
                </Stack>
                <Stack width="50%" minWidth="140px" flexGrow={100}>
                    <TableEntryValue data={data} highlightLabel={highlightLabel} />
                </Stack>
            </Stack>
            <Stack sx={{ width: "max-content" }}>
                <TableEntryIcon data={data} hasPlaceholderSpacing={hasIconPlaceholderSpacing} />
            </Stack>
        </Stack>
    );
};

const HorizontalEntry = ({
    data,
    hasIconPlaceholderSpacing,
    highlightLabel,
}: {
    data: TableCardEntry;
    hasIconPlaceholderSpacing: boolean;
    highlightLabel?: boolean;
}) => {
    return (
        <Stack direction="row" justifyContent="space-between" width={"100%"}>
            <Stack direction="row" width={"100%"}>
                <Stack width="180px">
                    <TableEntryLabel data={data} highlightLabel={highlightLabel} />
                </Stack>
                <Stack marginLeft={"8px"}>
                    <TableEntryValue data={data} highlightLabel={highlightLabel} />
                </Stack>
            </Stack>
            <Stack>
                <TableEntryIcon data={data} hasPlaceholderSpacing={hasIconPlaceholderSpacing} />
            </Stack>
        </Stack>
    );
};
